import { useAuth } from '@clerk/clerk-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AdminApiError } from '../../api/admin-client.js';
import {
  fetchAdminOrders,
  fetchAdminOrderDetail,
  changeAdminOrderStatus,
  cancelAdminOrder,
  fetchOrderCancellationReasons,
} from '../api/orders-api.js';
import type { OrderListParams, ChangeOrderStatusInput, CancelOrderInput } from '../types/orders.js';

const ORDERS_KEY = ['admin', 'orders'] as const;

function ordersListKey(params: OrderListParams) {
  return [...ORDERS_KEY, 'list', params] as const;
}

function orderDetailKey(orderNumber: string) {
  return [...ORDERS_KEY, 'detail', orderNumber] as const;
}

async function requireToken(getToken: () => Promise<string | null>): Promise<string> {
  const token = await getToken();
  if (!token) {
    throw new AdminApiError('AUTHENTICATION_ERROR', 'Your session has expired. Please sign in again.', 401);
  }
  return token;
}

function nonRetryableStatus(error: unknown): boolean {
  return error instanceof AdminApiError && [401, 403, 404, 422].includes(error.status);
}

export function useAdminOrders(params: OrderListParams) {
  const { isLoaded, isSignedIn, getToken } = useAuth();
  return useQuery({
    queryKey: ordersListKey(params),
    queryFn: async ({ signal }) => fetchAdminOrders(params, { token: await requireToken(getToken), signal }),
    enabled: isLoaded && isSignedIn === true,
    staleTime: 15_000,
    retry: (count, error) => !nonRetryableStatus(error) && count < 2,
  });
}

export function useAdminOrderDetail(orderNumber: string | undefined) {
  const { isLoaded, isSignedIn, getToken } = useAuth();
  return useQuery({
    queryKey: orderDetailKey(orderNumber ?? ''),
    queryFn: async ({ signal }) =>
      fetchAdminOrderDetail(orderNumber!, { token: await requireToken(getToken), signal }),
    enabled: isLoaded && isSignedIn === true && Boolean(orderNumber),
    staleTime: 10_000,
    retry: (count, error) => !nonRetryableStatus(error) && count < 2,
  });
}

export function useOrderCancellationReasons() {
  const { isLoaded, isSignedIn, getToken } = useAuth();
  return useQuery({
    queryKey: [...ORDERS_KEY, 'cancellation-reasons'] as const,
    queryFn: async ({ signal }) => fetchOrderCancellationReasons({ token: await requireToken(getToken), signal }),
    enabled: isLoaded && isSignedIn === true,
    staleTime: 5 * 60_000,
  });
}

/** Status change/cancel both invalidate the list + this order's detail -- never an optimistic update (Part 24 "avoid optimistic updates for order ... operations unless rollback is correct"), since the server is the sole authority on whether a transition was actually valid. */
export function useAdminOrderMutations() {
  const { getToken } = useAuth();
  const queryClient = useQueryClient();

  function invalidateAll() {
    void queryClient.invalidateQueries({ queryKey: ORDERS_KEY });
  }

  const changeStatus = useMutation({
    mutationFn: async ({ orderNumber, input }: { orderNumber: string; input: ChangeOrderStatusInput }) =>
      changeAdminOrderStatus(orderNumber, input, { token: await requireToken(getToken) }),
    onSuccess: invalidateAll,
  });

  const cancel = useMutation({
    mutationFn: async ({ orderNumber, input }: { orderNumber: string; input: CancelOrderInput }) =>
      cancelAdminOrder(orderNumber, input, { token: await requireToken(getToken) }),
    onSuccess: invalidateAll,
  });

  return { changeStatus, cancel };
}
